import React, { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { API_ENDPOINTS } from '../config/api'
import { Button, Input, Alert, Card } from '../components/ui'

const ResetPasswordPage: React.FC = () => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password !== confirm) {
      setError(intl.formatMessage({ id: 'auth.reset.mismatch' }))
      return
    }
    if (password.length < 6) {
      setError(intl.formatMessage({ id: 'auth.validation.passwordMin' }))
      return
    }
    setIsLoading(true)
    setError('')
    try {
      const response = await fetch(API_ENDPOINTS.auth.resetPassword, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || intl.formatMessage({ id: 'auth.reset.failed' }))
      }
      setSuccess(true)
    } catch (err: any) {
      setError(err.message || intl.formatMessage({ id: 'auth.reset.genericError' }))
    } finally {
      setIsLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
        <div className="max-w-md w-full text-center">
          <Alert variant="error"><FormattedMessage id="auth.reset.invalidLink" /></Alert>
          <div className="mt-4">
            <Link to="/forgot-password" className="text-sm text-primary hover:text-primary font-medium">
              <FormattedMessage id="auth.reset.requestNew" />
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
        <div className="max-w-md w-full text-center">
          <Card padding="lg">
            <div className="text-4xl mb-4">✅</div>
            <h2 className="text-xl font-bold text-foreground mb-2"><FormattedMessage id="auth.reset.successTitle" /></h2>
            <p className="text-sm text-foreground-muted mb-6"><FormattedMessage id="auth.reset.successBody" /></p>
            <Button variant="primary" onClick={() => window.location.href = '/auth/signin'}>
              <FormattedMessage id="auth.reset.signIn" />
            </Button>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
      <div className="max-w-md w-full space-y-6">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-foreground"><FormattedMessage id="auth.reset.title" /></h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="error">{error}</Alert>
          )}

          <Input
            label={intl.formatMessage({ id: 'auth.reset.newPassword' })}
            id="new-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            placeholder={intl.formatMessage({ id: 'auth.passwordPlaceholder' })}
          />

          <Input
            label={intl.formatMessage({ id: 'auth.reset.confirmPassword' })}
            id="confirm-password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            placeholder={intl.formatMessage({ id: 'auth.reset.confirmPassword' })}
          />

          <Button type="submit" fullWidth loading={isLoading}>
            {isLoading
              ? intl.formatMessage({ id: 'auth.reset.resetting' })
              : intl.formatMessage({ id: 'auth.reset.submit' })}
          </Button>
        </form>
      </div>
    </div>
  )
}

export default ResetPasswordPage
